import WithoutResults from '../withoutResults/withoutResults';
import CardResults from './cardResults';

/**
 * Componente coordinador para mostrar resultados de búsqueda con manejo de estados
 * 
 * @description Implementa el patrón de composición para manejar diferentes estados
 * de resultados de búsqueda. Actúa como coordinador que decide qué componente
 * renderizar basado en la disponibilidad de datos, delegando la responsabilidad
 * de renderizado a componentes especializados.
 * 
 * **Estados manejados:**
 * - **Sin resultados**: Datos vacíos o inválidos → WithoutResults
 * - **Con resultados**: Array válido con elementos → CardResults
 * 
 * **Validación defensiva:**
 * - Maneja `data` null/undefined de forma segura
 * - Valida que `data.hits` sea un array válido
 * - Proporciona fallback seguro para datos corruptos
 * 
 * @component
 * @param {Object} props - Propiedades del componente
 * @param {Object|null} props.data - Objeto de respuesta de la API de búsqueda
 * @param {Array} props.data.hits - Array de resultados de búsqueda
 * @param {number} props.data.total - Total de resultados disponibles
 * @param {number} props.data.from - Offset de paginación actual
 * @param {string} props.query - Término de búsqueda ingresado por el usuario
 * 
 * @returns {JSX.Element} WithoutResults o CardResults según disponibilidad de datos
 * 
 * @example
 * // Estado sin resultados - muestra mensaje amigable
 * <Results 
 *   data={null} 
 *   query="termino sin coincidencias" 
 * />
 * // Renderiza: <WithoutResults query="termino sin coincidencias" />
 * 
 * @example
 * // Estado con resultados - muestra lista de tarjetas
 * <Results 
 *   data={{
 *     hits: [
 *       { id: 1, title: "Resultado 1", ... },
 *       { id: 2, title: "Resultado 2", ... }
 *     ],
 *     total: 150,
 *     from: 0
 *   }}
 *   query="búsqueda exitosa" 
 * />
 * // Renderiza: <CardResults data={[...]} />
 * 
 * @example
 * // Integración con BuscadorApp
 * const BuscadorApp = () => {
 *   const [data, setData] = useState(null);
 *   const [query, setQuery] = useState('');
 *   
 *   return (
 *     <div>
 *       <SearchForm onSearch={setQuery} />
 *       {!showLoading && (
 *         <Results data={data} query={query} />
 *       )}
 *     </div>
 *   );
 * };
 * 
 * @example
 * // Casos de validación defensiva manejados:
 * <Results data={undefined} query="test" />           // → WithoutResults
 * <Results data={null} query="test" />               // → WithoutResults  
 * <Results data={{}} query="test" />                 // → WithoutResults
 * <Results data={{hits: null}} query="test" />       // → WithoutResults
 * <Results data={{hits: "invalid"}} query="test" />  // → WithoutResults
 * <Results data={{hits: []}} query="test" />         // → WithoutResults
 * <Results data={{hits: [item]}} query="test" />     // → CardResults
 * 
 * @example
 * // Estructura esperada de data (respuesta de API):
 * const apiResponse = {
 *   hits: [                    // Array de resultados
 *     {
 *       id: string,
 *       title: string,
 *       description: string,
 *       url: string,
 *       image?: string,
 *       category?: string,
 *       publishedAt?: string
 *     }
 *   ],
 *   total: number,           // Total de resultados disponibles
 *   from: number,            // Offset actual para paginación
 *   size: number             // Tamaño de página utilizado
 * };
 * 
 * @since 1.0.0
 * @author Valeria Garcia
 * 
 * @see {@link ./cardResults.js} Componente para renderizar lista de resultados
 * @see {@link ../withoutResults/withoutResults.js} Componente para estado sin resultados
 * @see {@link ../BuscadorApp.js} Componente padre que utiliza Results
 * @see {@link ../../services/getSearch.js} Servicio que proporciona la estructura de data
 */
export default function Results({ data, query }) {
  const dataQ = data && Array.isArray(data.hits) ? data.hits : [];

  return dataQ.length === 0
    ? <WithoutResults query={query} />
    : <CardResults data={dataQ} />;
}